import React, { useCallback, useEffect } from 'react'
import { useState } from 'react'
import { Message } from 'rsuite'
import useStandardToaster from '../../../ui_reusable/StandardToaster'

import VersatileGrid from '../../../ui_reusable/VersatileGrid'
import {
  GridCellBoldText,
  GridCellNormalText,
  GridCellSmallFadedText,
  ListInVersatileGrid,
  ListItemInVersatileGrid,
} from '../../../ui_reusable/styles/VersatileGridUtils.styles'
import { StandardToggleButton } from '../../../ui_reusable/styles/ToggleButton.styles'
import { StandardCheckbox } from '../../../ui_reusable/StandardInputs'
import { ToggleContainer } from './styles/ProjectUserList.styles'
import { VerySmallNormalTag } from '../../../ui_reusable/styles/Tags.styles'

// Server-side pagination: the grid shows one page, the API owns the full list.
// PAGE_SIZE doubles as VersatileGrid `maxDisplayedItems`.
const PAGE_SIZE = 10

const ProjectUserList = ({ projectId }) => {
  const [projectUsers, setProjectUsers] = useState([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1) // 1-based, matches `?page=` API contract
  const [search, setSearch] = useState('')
  const [refresh, setRefresh] = useState(new Date().getTime())
  const [loading, setLoading] = useState(true)

  const { pushToast } = useStandardToaster()
  const id = projectId

  // Flatten project users data for search functionality
  const flattenProjectUsersData = (users) => {
    return users.map((userData) => ({
      ...userData,
      id: userData.client_user.user.id,
      name: userData.client_user.user.name,
      email: userData.client_user.user.email,
      phone: userData.client_user.user.phone,
      role: userData.client_user.role,
      solutions: userData.project_solutions
        .map((ps) => ps.solution.name)
        .join(', '),
      // Original data preserved for rendering and actions
      _original: userData,
    }))
  }

  // Fetch one page of project users
  useEffect(() => {
    const abortController = new AbortController()

    const getProjectUsers = async () => {
      if (!id) {
        setLoading(false)
        return
      }
      setLoading(true)
      try {
        const params = new URLSearchParams({
          limit: String(PAGE_SIZE),
          page: String(page),
        })
        if (search) {
          params.set('search', search)
        }
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/projects/${id}/users?${params.toString()}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
            signal: abortController.signal,
          },
        )

        if (response.status === 404 && page > 1) {
          setPage(page - 1)
          return
        }

        const res = await response.json()
        const users = res.data || []

        setProjectUsers(flattenProjectUsersData(Array.isArray(users) ? users : []))
        setTotal(res.total ?? (Array.isArray(users) ? users.length : 0))
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Failed to fetch project users:', error)
          setProjectUsers([])
        }
      } finally {
        if (!abortController.signal.aborted) {
          setLoading(false)
        }
      }
    }

    getProjectUsers()

    return () => {
      abortController.abort()
    }
  }, [id, refresh, page, search])

  // Handle scenario access toggle
  const handleToggleScenarioAccess = async (userId, scenario_access) => {
    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/api/projects/${id}/users/${userId}`,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        method: 'PATCH',
        body: JSON.stringify({ has_scenario_access: scenario_access }),
      },
    )

    if (response.status === 200) {
      setRefresh(new Date().getTime())
      pushToast(
        <Message type="success">Scenario access updated successfully.</Message>,
        { placement: 'topCenter', duration: 5000 },
      )
    } else {
      pushToast(
        <Message type="error">Failed to update scenario access.</Message>,
        { placement: 'topCenter', duration: 5000 },
      )
    }
  }

  // Add new handler for solution access toggle
  const handleSolutionAccessToggle = async (
    userId,
    solutionId,
    currentAccess,
  ) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/projects/${id}/users/${userId}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
          body: JSON.stringify({
            solution: {
              project_solution_id: solutionId,
              access: !currentAccess,
            },
          }),
        },
      )

      if (response.status === 200) {
        setRefresh(new Date().getTime())
        pushToast(
          <Message type="success">
            Solution access updated successfully.
          </Message>,
          { placement: 'topCenter', duration: 5000 },
        )
      } else {
        pushToast(
          <Message type="error">Failed to update solution access.</Message>,
          { placement: 'topCenter', duration: 5000 },
        )
      }
    } catch (error) {
      console.error('Failed to update solution access:', error)
      pushToast(
        <Message type="error">Failed to update solution access.</Message>,
        { placement: 'topCenter', duration: 5000 },
      )
    }
  }

  // Grid configuration
  const headers = [
    {
      title: 'Name',
      key: 'name',
      type: 'string',
      isSortable: false,
      isSearchable: true,
    },
    {
      title: 'Role',
      key: 'role',
      type: 'string',
      isSortable: false,
      isSearchable: false,
      borderLeft: false,
    },
    {
      title: 'Email',
      key: 'email',
      type: 'string',
      isSortable: false,
      isSearchable: false,
    },
    {
      title: 'Phone',
      key: 'phone',
      type: 'string',
      isSortable: false,
      isSearchable: false,
    },

    {
      title: 'Solution Access',
      key: 'solutions',
      type: 'string',
      isSortable: false,
      isSearchable: false,
      borderLeft: true,
    },
    {
      title: 'Scenario Access',
      key: 'has_scenario_access',
      type: 'boolean',
      isSortable: false,
      isSearchable: false,
      borderLeft: true,
    },
  ]

  const handlePageChange = useCallback((nextPage) => {
    setPage(nextPage)
  }, [])

  const handleSearchChange = useCallback((term) => {
    setPage(1)
    setSearch(term)
  }, [])

  return (
    <VersatileGrid
      headers={headers}
      data={projectUsers}
      renderRow={(rowData) => [
        <GridCellBoldText>{rowData.name}</GridCellBoldText>,
        <VerySmallNormalTag>{rowData.role}</VerySmallNormalTag>,
        <GridCellNormalText>{rowData.email}</GridCellNormalText>,
        <GridCellSmallFadedText>{rowData.phone}</GridCellSmallFadedText>,
        <ListInVersatileGrid>
          {rowData._original.project_solutions.map((project_solution, i) => (
            <ListItemInVersatileGrid
              key={i}
              style={{ display: 'flex', alignItems: 'center' }}
            >
              <StandardCheckbox
                checked={project_solution.has_access}
                onChange={() =>
                  handleSolutionAccessToggle(
                    rowData.id,
                    project_solution.id,
                    project_solution.has_access,
                  )
                }
              ></StandardCheckbox>
              <GridCellSmallFadedText>
                {project_solution.solution.name}
              </GridCellSmallFadedText>
            </ListItemInVersatileGrid>
          ))}
        </ListInVersatileGrid>,
        <ToggleContainer>
          <StandardToggleButton
            checked={rowData._original.has_scenario_access}
            onChange={() =>
              handleToggleScenarioAccess(
                rowData.id,
                !rowData._original.has_scenario_access,
              )
            }
          />
        </ToggleContainer>,
      ]}
      maxDisplayedItems={PAGE_SIZE}
      serverSide
      serverTotal={total}
      serverPage={page}
      onPageChange={handlePageChange}
      onSearchChange={handleSearchChange}
      loading={loading}
      loadingMessage={'Loading users...'}
    />
  )
}

export default ProjectUserList
